import { FC, useContext } from "react";

import {
  Analytics,
  Edit,
  Group,
  PageviewOutlined,
  PostAdd,
  PrivacyTip,
  School,
  Share,
} from "@mui/icons-material";
import {
  Badge,
  IconButton,
  Theme,
  Tooltip,
  useMediaQuery,
  ListItemIcon,
  ListItemText,
  MenuItem,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import { NavLink } from "react-router-dom";

import { BULLETIN_START_ROUTE } from "@/components/alerts/add-v2/routePaths";
import { AuthStateContext } from "@/contexts/AuthContext";
import { useSidebar } from "@/contexts/SidebarContext";
import { usePendingBulletinShareCount } from "@/hooks/usePendingBulletinShareCount";

import { collapsedAppIconSx } from "./collapsedAppIconSx";
import { frameNavItemSx } from "./frameNavItemSx";

interface Props {
  dense?: boolean;
  logout?: () => void;
  onMenuClick?: () => void;
  collapsed?: boolean;
}

const RubiBoloMenu: FC<Props> = ({ dense = false, collapsed = false }) => {
  const theme = useTheme();
  const isSmall = useMediaQuery((theme as Theme).breakpoints.down("sm"));
  const { setCollapsed } = useSidebar();
  const { t } = useTranslation("bulletin/menu");
  const hideSideBar = () => {
    setCollapsed(true);
  };

  const menuItems = [
    {
      to: "/rubialert/alerts",
      text: t("bulletinBoard"),
      icon: <PageviewOutlined />,
    },
    {
      to: "/rubialert/bulletin-share",
      text: t("bulletinShare"),
      icon: <Share />,
    },
    // The start screen covers both of what used to be two entries here: its first card starts a
    // bulletin from scratch, its second uploads one. `/rubialert/add-alert` and
    // `/rubialert/upload-alert` still resolve, but nothing links to them any more.
    {
      to: BULLETIN_START_ROUTE,
      text: t("addNew"),
      icon: <PostAdd />,
    },
    {
      to: "/rubialert/drafts",
      text: t("drafts"),
      icon: <Edit />,
    },
    {
      to: "/rubialert/analytics",
      text: t("dashboard"),
      icon: <Analytics />,
    },
    {
      to: "/rubialert/manage-groups",
      text: t("groups"),
      icon: <Group />,
    },
    {
      to: "/rubialert/training",
      text: t("training"),
      icon: <School />,
    },
  ];
  const { checkPermission } = useContext(AuthStateContext);
  const canManageBulletinSharing = checkPermission(
    "manage",
    "bulletin_sharing",
  );
  const { data: pendingShareCount = 0 } = usePendingBulletinShareCount(
    canManageBulletinSharing,
  );

  return (
    <div className="menu">
      {!collapsed ? (
        <div className="sidebar-top">
          <div className="app-name">
            Rubi<span>ALERT</span>
          </div>
        </div>
      ) : (
        <div className="application-item">
          <Tooltip title="Bulletin Management System" placement="right" arrow>
            <IconButton
              aria-label="RubiAlert App"
              size="large"
              disableRipple
              sx={collapsedAppIconSx}
            >
              <PrivacyTip />
            </IconButton>
          </Tooltip>
        </div>
      )}
      <div className="left-menu">
        {menuItems.map((item) => (
          <Tooltip
            key={item.to}
            title={collapsed ? item.text : ""}
            placement="right"
            arrow
          >
            <MenuItem
              component={NavLink}
              to={item.to}
              // The badge count is only visual; name it so screen readers announce it too.
              aria-label={
                item.to === "/rubialert/bulletin-share" && pendingShareCount > 0
                  ? t("bulletinSharePending", { count: pendingShareCount })
                  : undefined
              }
              onClick={isSmall ? hideSideBar : undefined}
              sx={[
                frameNavItemSx(),
                {
                  pl: collapsed ? 1 : 2,
                  py: dense ? 1 : 1.5,
                  minHeight: collapsed ? 48 : "auto",
                  justifyContent: collapsed ? "center" : "flex-start",
                },
              ]}
            >
              <ListItemIcon
                sx={{
                  minWidth: collapsed ? 0 : 40,
                  mr: collapsed ? 0 : 2,
                  justifyContent: "center",
                }}
              >
                {item.to === "/rubialert/bulletin-share" ? (
                  <Badge badgeContent={pendingShareCount} color="error">
                    {item.icon}
                  </Badge>
                ) : (
                  item.icon
                )}
              </ListItemIcon>
              {!collapsed && <ListItemText primary={item.text} />}
            </MenuItem>
          </Tooltip>
        ))}
      </div>
    </div>
  );
};

export default RubiBoloMenu;
